import { InputNumber, message } from 'antd';
import {
  Clipboard,
  ExternalLink,
  FileArchive,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { brandStore, roomDebugLogsStore } from '@/stores';
import type { ExportRoomDebugLogsReply } from '@/types';
import { isNextopBrand } from '@/utils/brand';

const BYTES_PER_MIB = 1024 * 1024;

function toNumber(value: number | string) {
  return typeof value === 'number' ? value : Number(value);
}

function formatBytes(value: number | string) {
  const numericValue = toNumber(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return '-';
  }

  if (numericValue < 1024) {
    return `${numericValue} B`;
  }

  const units = ['KiB', 'MiB', 'GiB'];
  let size = numericValue / 1024;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value || '-';
  }

  return date.toLocaleString('zh-CN');
}

function formatDuration(ms: number | string) {
  const numericMs = toNumber(ms);
  if (!Number.isFinite(numericMs) || numericMs < 0) {
    return '-';
  }

  if (numericMs < 1000) {
    return `${numericMs} ms`;
  }

  return `${(numericMs / 1000).toFixed(2)} s`;
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-slate-50 p-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 break-all font-mono text-sm text-foreground">
        {value || '-'}
      </p>
    </div>
  );
}

function ExportResult({ result }: { result: ExportRoomDebugLogsReply }) {
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(result.downloadUrl);
      message.success('下载链接已复制');
    } catch (_error) {
      message.error('复制失败，请手动复制链接');
    }
  };

  return (
    <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FileArchive className="h-4 w-4 text-accent" />
            <h2 className="text-base font-semibold text-foreground">
              导出完成
            </h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            链接会在 {formatDateTime(result.expiresAt)} 过期。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleCopyUrl}>
            <Clipboard className="mr-2 h-4 w-4" />
            复制链接
          </Button>
        </div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <DetailItem label="Room ID" value={result.roomId} />
        <DetailItem label="Workspace ID" value={result.workspaceId} />
        <DetailItem label="Sandbox ID" value={result.sandboxId} />
        <DetailItem label="Export ID" value={result.exportId} />
        <DetailItem label="Object Key" value={result.objectKey} />
        <DetailItem
          label="Archive Size"
          value={formatBytes(result.archiveBytes)}
        />
        <DetailItem label="SHA-256" value={result.sha256} />
        <DetailItem label="Pack Time" value={formatDuration(result.packMs)} />
        <DetailItem
          label="Upload Time"
          value={formatDuration(result.uploadMs)}
        />
      </div>
    </div>
  );
}

export const RoomDebugLogsPage = observer(() => {
  const [roomId, setRoomId] = useState('');
  const [maxSizeMiB, setMaxSizeMiB] = useState<number | null>(100);
  const activeBrand = brandStore.activeBrand;
  const available = isNextopBrand(activeBrand);

  const maxSizeBytes = useMemo(() => {
    if (maxSizeMiB === null || maxSizeMiB === undefined) {
      return undefined;
    }

    if (maxSizeMiB <= 0) {
      return 0;
    }

    return Math.trunc(maxSizeMiB * BYTES_PER_MIB);
  }, [maxSizeMiB]);

  const handleExport = async () => {
    if (!available) {
      message.warning('当前能力仅对 nextop Brand 开放');
      return;
    }

    try {
      const result = await roomDebugLogsStore.exportLogs(roomId, maxSizeBytes);
      downloadArchive(result);
      message.success(`已导出 ${formatBytes(result.archiveBytes)} 日志归档`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '导出日志失败');
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="space-y-6 p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-lg font-semibold text-foreground">
                  Room Debug Logs
                </h1>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  {activeBrand?.name || '未选择 Brand'}
                </span>
              </div>
              <p className="max-w-3xl text-sm text-muted-foreground">
                输入 Room ID 后，后台会从绑定的 sandbox 导出 /tmp/log
                并生成短期下载链接。
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => roomDebugLogsStore.clearResult()}
              disabled={roomDebugLogsStore.exporting}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              清空结果
            </Button>
          </div>

          {!available ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              当前 Brand 不是 nextop，请切换到 nextop 后再使用日志导出能力。
            </div>
          ) : null}

          {roomDebugLogsStore.error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {roomDebugLogsStore.error}
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px_auto] lg:items-end">
            <div className="space-y-2">
              <Label htmlFor="room-debug-room-id">Room ID *</Label>
              <Input
                id="room-debug-room-id"
                value={roomId}
                onChange={(event) => setRoomId(event.target.value)}
                placeholder="room_123"
                disabled={!available || roomDebugLogsStore.exporting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="room-debug-max-size">最大归档大小（MiB）</Label>
              <InputNumber
                id="room-debug-max-size"
                value={maxSizeMiB}
                min={0}
                step={10}
                precision={0}
                className="w-full"
                disabled={!available || roomDebugLogsStore.exporting}
                onChange={(value) => setMaxSizeMiB(value)}
              />
            </div>
            <Button
              onClick={() => void handleExport()}
              disabled={
                !available || !roomId.trim() || roomDebugLogsStore.exporting
              }
              className="w-full lg:w-auto"
            >
              {roomDebugLogsStore.exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2 h-4 w-4" />
              )}
              {roomDebugLogsStore.exporting ? '导出中...' : '导出日志'}
            </Button>
          </div>
        </div>
      </div>

      {roomDebugLogsStore.lastExport ? (
        <ExportResult result={roomDebugLogsStore.lastExport} />
      ) : null}
    </div>
  );
});

function downloadArchive(result: ExportRoomDebugLogsReply) {
  const link = document.createElement('a');
  link.href = result.downloadUrl;
  link.download = `${result.roomId || 'room'}-debug-logs.tar.gz`;
  link.rel = 'noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}
